<template>
  <div
      class="pulse-button"
      :class="{
        '--hovered': hovered
      }"
      :style="computedStyles"
  >
    <q-btn
        class="button"
        @click="emit('click')"
        v-bind="btnBind"
        :ripple="{ center: true }"
        :style="buttonComputedStyles"
    >
      <div class="button-content">
        <q-icon
            :name="icon"
            :size="iconSize"
        />
      </div>
    </q-btn>
    <!-- Pulse эффект -->
    <div
        v-for="i in pulseLayers"
        :key="i"
        class="pulse-ring"
        :style="getPulseRingStyle(i)"
    ></div>
  </div>
</template>

<script setup lang="ts">
import {computed} from "vue";
import {QBtnProps} from "quasar";

const emit = defineEmits<{
  click: []
}>()

type Props = QBtnProps & {
  iconSize?: string;
  // Цвет градиента кнопки (если не задан, используется buttonColor)
  gradientStart?: string;
  gradientEnd?: string;
  // Цвет волн пульсации
  pulseColor?: string;
  // Цвет искр
  sparkleColor?: string;
  // Интенсивность пульсации (1 - нормальная, 2 - сильная, 0.5 - слабая)
  pulseIntensity?: number;
  // Количество слоев пульсации
  pulseLayers?: number;
  // Скорость пульсации (в секундах)
  pulseSpeed?: number;
  hovered?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  iconSize: '32px',
  color: "primary",
  gradientStart: "#667eea",
  gradientEnd: "#764ba2",
  pulseColor: "rgba(102, 126, 234, 0.7)",
  sparkleColor: "#ffd700",
  pulseIntensity: 1,
  pulseLayers: 1,
  pulseSpeed: 2,
  hovered: false
});

const btnBind = computed(() => {
  const result = {...props}

  for (const key of [
    'icon',
    'gradientStart',
    'gradientEnd',
    'pulseColor',
    'sparkleColor',
    'pulseIntensity',
    'pulseLayers',
    'pulseSpeed',
  ]) {
    delete result[key as keyof typeof result];
  }
  return result
})


/**
 * Получить стиль для кольца пульсации
 */
const getPulseRingStyle = (index: number) => {
  const delay = (index - 1) * (props.pulseSpeed / props.pulseLayers);
  const intensityMultiplier = 0.1 * props.pulseIntensity;

  return {
    borderColor: props.pulseColor,
    animationDuration: `${props.pulseSpeed}s`,
    animationDelay: `${delay}s`,
    animationTimingFunction: `cubic-bezier(0.215, 0.61, 0.355, ${1 - intensityMultiplier})`,
  };
};

const buttonComputedStyles = computed(() => {
  const res: any = {};

  return res
})

/**
 * Вычисляемые стили для корневого элемента
 */
const computedStyles = computed(() => {
  const intensity = props.pulseIntensity;

  return {
    '--gradient-start': props.gradientStart,
    '--gradient-end': props.gradientEnd,
    '--pulse-color': props.pulseColor,
    '--pulse-intensity': intensity,
    '--pulse-shadow-spread': `${8 * intensity}px`,
    '--pulse-ring-scale-end': `${1.4 + (intensity - 1) * 0.3}`,
    '--pulse-ring-opacity-start': `${Math.min(1, 0.7 * intensity)}`,
    '--button-shadow-blur': `${4 + (intensity - 1) * 2}px`,
  };
});


</script>

<style scoped lang="scss">
.pulse-button {
  // Определяем CSS переменные для использования в стилях
  --gradient-start: #667eea;
  --gradient-end: #764ba2;
  --pulse-color: rgba(102, 126, 234, 0.7);
  --pulse-intensity: 1;
  --pulse-shadow-spread: 8px;
  --pulse-ring-scale-end: 1.4;
  --pulse-ring-opacity-start: 0.7;
  --button-shadow-blur: 4px;
  position: relative;

  &.--hovered,
  &:hover {
    .button {
      transform: scale(1.1) rotate(15deg);
      box-shadow: 0 calc(var(--button-shadow-blur) * 1.5) 30px rgba(102, 126, 234, 0.6),
      0 0 0 0 var(--pulse-color);

      .button-content {
        animation: wiggle 0.5s ease;
      }
    }
  }
}

.button {
  background: linear-gradient(135deg, var(--gradient-start) 0%, var(--gradient-end) 100%);
  box-shadow: 0 var(--button-shadow-blur) 20px rgba(102, 126, 234, 0.4),
  0 0 0 0 var(--pulse-color);
  transition: all 0.3s ease;
  animation: pulse-shadow calc(v-bind(pulseSpeed) * 1s) infinite;
  position: relative;

  &:hover {

  }

  &:active {
    transform: scale(0.95);
  }
}

.button-content {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  animation: rotate 8s linear infinite;
}

.pulse-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  border: calc(3px * var(--pulse-intensity)) solid var(--pulse-color);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: pulse-ring-animation calc(v-bind(pulseSpeed) * 1s) infinite;
  pointer-events: none;
}

.tooltip-content {
  display: flex;
  align-items: center;
  font-weight: 500;
}

@keyframes pulse-shadow {
  0%,
  100% {
    box-shadow: 0 var(--button-shadow-blur) 20px rgba(102, 126, 234, 0.4),
    0 0 0 0 var(--pulse-color);
  }
  50% {
    box-shadow: 0 var(--button-shadow-blur) 20px rgba(102, 126, 234, 0.4),
    0 0 0 var(--pulse-shadow-spread) rgba(102, 126, 234, 0);
  }
}

@keyframes pulse-ring-animation {
  0% {
    transform: translate(-50%, -50%) scale(0.9);
    opacity: var(--pulse-ring-opacity-start);
  }
  100% {
    transform: translate(-50%, -50%) scale(var(--pulse-ring-scale-end));
    opacity: 0;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes wiggle {
  0%,
  100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(-15deg);
  }
  75% {
    transform: rotate(15deg);
  }
}

</style>
